import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageBreadcrumb from "@/components/PageBreadcrumb";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SeoHead
        title="Privacy & Cookie Policy - AI Lab Sweden AB"
        description="Privacy and Cookie Policy for AI Lab Sweden AB. Learn how we collect, use, and protect your personal data in accordance with GDPR."
        path="/privacy-policy"
      />
      
      <Header />
      
      <main className="flex-1">
        <PageBreadcrumb currentPage="Privacy & Cookie Policy" />
        
        <section className="py-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-8">Privacy & Cookie Policy</h1>
            
            <div className="prose prose-invert max-w-none space-y-8">
              <div>
                <p className="text-muted-foreground mb-6">
                  Last updated: 28 November 2025
                </p>
              </div>

              {/* Section 1: Introduction */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Introduction & About this Policy</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <p className="text-muted-foreground">
                    AI Lab Sweden AB (reg. no. 559415-2919), Kungsbro Strand 21, 112 26 Stockholm Sverige, ("AI-labbet", "we", "our" or "us") is the data controller for the personal data outlined in this Privacy & Cookie Policy. For any Privacy Inquiries and Data Rights Requests, you can contact us at <a href="mailto:info@ailabbet.com" className="text-primary hover:underline">info@ailabbet.com</a>. We process your personal data in accordance with the General Data Protection Regulation (EU) 2016/679 (GDPR) and other applicable data protection laws.
                  </p>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">1.1 Purpose</h3>
                    <p className="text-muted-foreground mb-2">The purpose of this policy is to explain:</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                      <li>what personal data we collect;</li>
                      <li>why we collect it and the legal bases we rely on;</li>
                      <li>how we use and safeguard it;</li>
                      <li>who we share it with and when we transfer it outside the EU/EEA;</li>
                      <li>how long we keep it; and</li>
                      <li>your rights and how to exercise them.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">1.2 Scope</h3>
                    <p className="text-muted-foreground">
                      This Policy applies to visitors to our website (<a href="https://www.ailabbet.com/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://www.ailabbet.com/</a>), individuals who have signed up for our newsletter and other marketing activities as well as any other individuals whose personal data we may process when providing our services.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Definitions */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Definitions</h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <ul className="space-y-3 text-muted-foreground">
                    <li><strong className="text-foreground">Personal Data:</strong> Any information relating to an identified or identifiable natural person.</li>
                    <li><strong className="text-foreground">Processing:</strong> Any operation performed on personal data, such as collection, storage, use, disclosure, or deletion.</li>
                    <li><strong className="text-foreground">Data Controller (or Controller):</strong> The party that determines the purpose and means of the processing of personal data (AI-labbet is the Data Controller for the Personal Data covered by this Policy).</li>
                    <li><strong className="text-foreground">Data Processor (or Processor):</strong> The party that processes personal data on behalf of the controller.</li>
                    <li><strong className="text-foreground">Data Subject:</strong> The identified or identifiable natural person whose Personal Data is being processed.</li>
                  </ul>
                  <p className="text-muted-foreground mt-4">
                    Unless a term is expressly defined above, the meaning given to that term in the GDPR applies.
                  </p>
                </div>
              </section>

              {/* Section 3: Data We Collect */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Data We Collect and Why</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-6">
                  <p className="text-muted-foreground">
                    This section describes the types of personal data we collect, the purposes for which we use it, the legal bases we rely on, and how long we retain your data.
                  </p>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">3.1 Data Categories</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Technical and Cookie Data:</h4>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                          <li>Cookie data in the form of IP-address, browser type, ID device type, country and language, access time</li>
                          <li>Your cookie selection</li>
                          <li>How you interact with our website and its content and time</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Newsletter and Marketing Data:</h4>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                          <li>Name, contact details as e-mail address, phone number, potential employer, role and title.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">3.2 Purpose and Legal Basis</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Technical and Cookie Data:</h4>
                        <p className="text-muted-foreground mb-2">
                          To ensure our website is effective, functional, and informative, and to market our business and services to clients, business partners, and potential business opportunities.
                        </p>
                        <p className="text-muted-foreground mb-2">
                          The use of cookies is based on consent, except for essential necessary cookies for the functionality of our website.
                        </p>
                        <p className="text-muted-foreground">
                          Our use of personal information collected via cookies is based on our legitimate interest under GDPR Art 6(1)(f) in maintaining an effective website and marketing our business and services to clients, business partners, and potential business opportunities.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Newsletter and Marketing Data:</h4>
                        <p className="text-muted-foreground mb-2">
                          Our interest lies in promoting our business, offerings, and brand through marketing campaigns, as well as providing relevant newsletters and articles.
                        </p>
                        <p className="text-muted-foreground mb-2">
                          Disclosure of marketing activities directly to you is based on your consent.
                        </p>
                        <p className="text-muted-foreground">
                          Our processing of your data in our CRM systems is based on our legitimate interests in managing daily client and business relationships (for example, fulfilling your requests for newsletters and marketing materials), as well as accounting and financial tasks, in accordance with GDPR Article 6(1)(f).
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">3.3 Data Retention</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Technical and Cookie Data:</h4>
                        <p className="text-muted-foreground">
                          Cookie data is stored for the duration of the session and for periods as further detailed below in section 7, "Cookies."
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Newsletter and Marketing Data:</h4>
                        <p className="text-muted-foreground mb-2">
                          Data are stored until they are no longer needed for the provision of services to you, or until you withdraw your consent, or if your contact data are no longer active. However, data may be retained for a longer period if required by law, such as for bookkeeping purposes, which mandates storage for five (5) years.
                        </p>
                        <p className="text-muted-foreground">
                          Once the retention periods expire or data is no longer needed for its purpose, it will be securely deleted or anonymized. Anonymized data may be kept for statistics and operational analysis.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4: Sharing and International Transfers */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Sharing Your Personal Data and International Transfers</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <p className="text-muted-foreground">
                    We never sell your personal data. We only share your personal data with the categories of third parties listed below.
                  </p>
                  
                  <ul className="space-y-2 text-muted-foreground ml-4">
                    <li><strong className="text-foreground">Service Providers:</strong> We may engage trusted third-party service providers to perform functions and provide services (e.g., IT hosting, maintenance, cloud services, payment processing, marketing tools, and CRM). These providers will only process your personal data on our instructions and for the purposes listed above (see section 3.2, "Purpose and Legal Basis").</li>
                    <li><strong className="text-foreground">Legal Authorities:</strong> We may disclose your personal data if required to do so by law or in response to valid requests by public authorities.</li>
                  </ul>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">4.1 International Transfers</h3>
                    <p className="text-muted-foreground mb-2">
                      We may transfer your personal data to service providers located outside the European Union (EU) or European Economic Area (EEA), we will ensure that appropriate safeguards are in place to protect your data to a standard equivalent to that within the EU/EEA. These safeguards may include:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                      <li>Ensuring the country has been deemed by the European Commission to provide an adequate level of data protection.</li>
                      <li>Implementing Standard Contractual Clauses (SCCs) approved by the European Commission with the recipient.</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 5: Your Rights */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Your Data Protection Rights</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <p className="text-muted-foreground">
                    Under the GDPR, you have several rights concerning your personal data. These include:
                  </p>
                  
                  <ul className="space-y-2 text-muted-foreground ml-4">
                    <li><strong className="text-foreground">Right to Information:</strong> You have the right to request copies of your personal data that we hold.</li>
                    <li><strong className="text-foreground">Right to Rectification:</strong> You have the right to request correction or completion of any information you believe is inaccurate or incomplete.</li>
                    <li><strong className="text-foreground">Right to Erasure:</strong> You have the right to request that we erase your personal data.</li>
                    <li><strong className="text-foreground">Right to Limitation of Processing:</strong> You have the right to request that we restrict the processing of your personal data.</li>
                    <li><strong className="text-foreground">Data Portability:</strong> You have the right to request that we transfer your personal data to another controller or directly to you.</li>
                    <li><strong className="text-foreground">Right to Object:</strong> You have the right to object to our processing of your personal data based on our legitimate interests.</li>
                    <li><strong className="text-foreground">Right to Withdraw Consent:</strong> If we are processing your personal data based on your consent, you have the right to withdraw that consent at any time.</li>
                    <li><strong className="text-foreground">Right to file a complaint:</strong> You have the right to file a complaint to the Swedish supervisory authority, Integritetsmyndigheten (IMY).</li>
                  </ul>

                  <p className="text-muted-foreground">
                    Some of these rights may only be available under certain conditions. To exercise any of these rights, contact us using the contact information in Section 9. We may need to verify your identity before we act.
                  </p>
                </div>
              </section>

              {/* Section 6: Data Security */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Data Security</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <p className="text-muted-foreground">
                    We are committed to ensuring that your personal data is secure. We have implemented appropriate technical and organizational security measures to prevent unauthorized access, use, alteration, disclosure, or loss of your personal data. These measures include, but are not limited to:
                  </p>
                  
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Restricting access to personal data to authorized personnel only.</li>
                    <li>Using secure servers and networks.</li>
                    <li>Ensuring our third-party service providers have adequate security measures in place.</li>
                  </ul>

                  <p className="text-muted-foreground">
                    While we strive to protect your personal data, no method of electronic transmission or storage is ever completely secure, and we therefore cannot guarantee absolute security.
                  </p>
                </div>
              </section>

              {/* Section 7: Cookies */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Cookies</h2>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <p className="text-muted-foreground">
                    A cookie is a small file containing a string of characters that is sent to your device when you visit a website. When you return, the cookie lets the site recognize your browser, so it can retrieve information you previously provided. Cookies can store your preferences and other data to improve your experience on the site, or they can be used to track you across other websites.
                  </p>
                  
                  <p className="text-muted-foreground">
                    How long cookies stay on your device depends on their type. Persistent cookies remain until their expiration date or until you delete them, while session cookies expire when you close your browser.
                  </p>

                  <p className="text-muted-foreground">
                    You have the right to decide whether to allow non-essential cookies. You can at all times select and change your preferences in the banner that appears when you arrive on our website or by clicking the "Cookies" link in the footer.
                  </p>
                </div>
              </section>

              {/* Section 8: Changes to This Policy */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Changes to This Policy</h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted-foreground">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any significant changes by posting the new policy on our website and updating the "Last Updated" date at the top of this policy.
                  </p>
                </div>
              </section>

              {/* Section 9: Contact Information */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Contact Information</h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted-foreground mb-4">
                    If you have any questions, concerns, or requests regarding this Privacy Policy or our processing of your personal data, please do not hesitate to contact us:
                  </p>
                  
                  <div className="text-muted-foreground space-y-1">
                    <p><strong className="text-foreground">AI Lab Sweden AB</strong>, reg. no. 559415-2919</p>
                    <p>Adress: Kungsbro Strand 21, 112 26 Stockholm Sverige</p>
                    <p>
                      Email for privacy inquiries & data rights requests:{" "}
                      <a href="mailto:info@ailabbet.com" className="text-primary hover:underline">
                        info@ailabbet.com
                      </a>
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
